import * as React from 'react';
import { FlexBox, Visible, Span, Li } from '@vp/swan';
import { Account } from './Account';
import TrackableLink from '../TrackableLink';
import { HeaderLinkProps } from '../../types';
import { Image } from '../Image';
import {
  CartTenantDetails,
  MiniCart,
  getCartTenantDetails,
  invalidateMiniCartData
} from '@vp/cart-client';
import { TrackingContext } from '../../hooks/useTracking';
import { Logger } from '../../utils/logger';
import { FavoritesHeader } from '@vp/my-favorites-header';
import { ErrorBoundary } from '../ErrorBoundary';

declare global {
  interface Document {
    invalidateMiniCartData?: typeof invalidateMiniCartData;
  }
}

export const HeaderLinks = (props: HeaderLinkProps) => {
  const {
    homeUrl,
    accountLink,
    carePhoneNumber,
    cartLink,
    headerLinksLabel,
    helpLink,
    auth,
    cartIconComponent,
    myProjectsLink,
    siteHeaderHelpLinkA11yText,
    locale,
    suppressError,
    isUbik
  } = props;
  const [cartTenantDetails, setCartTenantDetails] = React.useState({});
  const [useMiniCartProp, setUseMiniCartProp] = React.useState(true);
  const [isAuthInitialized, setAuthInitialized] = React.useState(false);

  const { pageName, pageStage, pageSection } = React.useContext(TrackingContext);

  const minicartComponent =
    (cartTenantDetails && (
      <MiniCart
        tenantDetails={cartTenantDetails as CartTenantDetails}
        locale={locale}
        auth={auth}
        pageName={pageName}
        pageSection={pageSection}
        pageStage={pageStage}
      />
    )) ??
    null;

  const getCartComponent = () => {
    // cartIconComponent will be present incase of ESI bookends
    if (cartIconComponent) {

      // -- TODO : remove this temporary code later -- // 
      const cartIconComponentCartURL = cartIconComponent.props?.tenantDetails?.cartUrl;
      const cartIconComponentAbsoluteCartUrl = cartIconComponent.props?.tenantDetails?.absoluteCartUrl;

      if (cartIconComponentCartURL?.includes("undefined") || cartIconComponentAbsoluteCartUrl?.includes("undefined")) {
        Logger.instance.info("cartIconComponent will be loaded with undefined in the url", {
          cartIconComponentCartURL,
          cartIconComponentAbsoluteCartUrl,
          bookendsKey : document?.getElementById("bookendsHeader")?.getAttribute("data-headerkey"),
        });
      }

      // -- TODO : remove this temporary code later -- // 

      return cartIconComponent;
    } else if (minicartComponent) {

      // -- TODO : remove this temporary code later -- // 
      const miniCartComponentCartURL = minicartComponent.props?.tenantDetails?.cartUrl;
      const minicartComponentAbsoluteCartUrl = minicartComponent.props?.tenantDetails?.absoluteCartUrl;

      if (miniCartComponentCartURL?.includes("undefined") || minicartComponentAbsoluteCartUrl?.includes("undefined")) {
        Logger.instance.info("minicartComponent will be loaded with undefined in the url", {
          miniCartComponentCartURL,
          minicartComponentAbsoluteCartUrl,
          bookendsKey : document?.getElementById("bookendsHeader")?.getAttribute("data-headerkey"),
        });
      }
      // -- TODO : remove this temporary code later -- // 

      return minicartComponent;
    }
  };

  React.useEffect(() => {
    getCartTenantDetails('vistaprint', locale, 'production').then((result) => {
      setCartTenantDetails(result);
    });
  }, [locale]);

  React.useEffect(() => {
    setUseMiniCartProp(window?.bookends?.useMiniCart ?? true);
    if(isUbik){
      document.invalidateMiniCartData = invalidateMiniCartData;
    }
  }, [isUbik]);
  React.useEffect(() => {
    if (auth?.getIdentity()) {
      setAuthInitialized(true);
    }
  },[auth])

  // -- TODO : remove this temporary code later -- // 
  if (!getCartComponent() && cartLink?.url?.includes("undefined")) {
    Logger.instance.info(`Cart icon did not load from getCartComponent. Cart url from bookends data - ${cartLink?.url}`, {
      actualCartLogoURL : document.querySelector(".site-header-link-cart a")?.getAttribute("href"),
      bookendsKey : document?.getElementById("bookendsHeader")?.getAttribute("data-headerkey"),
    });
  }
  // -- TODO : remove this temporary code later -- // 
  
  return (
    <nav className="site-header-links" aria-label={headerLinksLabel}>
      <FlexBox
        as="ul"
        role="list"
        // role="list" is to account for Safari suppressing lists with the bullets removed
        // (see https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html)
        className="swan-list swan-list-skin-minimal"
        display="flex"
        alignItems="flex-start"
        flexDirection="row"
      >
        <Li padding={0} className="icon-alignment">
          <div className="site-header-link-menu nohover">
            <TrackableLink
              navigationDetailPath={[helpLink.analyticsId || 'help']}
              className="site-header-link site-header-link-content site-header-link-menu-top-item"
              href={helpLink.url}
              aria-label={siteHeaderHelpLinkA11yText}
            >
              {helpLink.image && (
                <Image
                  className="site-header-link-icon"
                  image={helpLink.image}
                  ariaHidden={true}
                />
              )}
              <Visible lg xl>
                <span className="site-header-link-text swan-text-1">
                  <span className="site-header-link-primary">
                    {helpLink.text}
                  </span>
                  {carePhoneNumber && (
                    <Span
                      textColor="subtle"
                      className="site-header-link-subtext"
                    >
                      {carePhoneNumber}
                    </Span>
                  )}
                </span>
              </Visible>
            </TrackableLink>
          </div>
        </Li>
        {myProjectsLink?.text && myProjectsLink?.url && (
          <Li padding={0} className="icon-alignment">
            <Visible sm md lg xl as="div" className="site-header-link-menu">
              <TrackableLink
                navigationDetailPath={[
                  myProjectsLink.analyticsId || 'myprojects',
                ]}
                className="site-header-link site-header-link-content site-header-link-menu-top-item"
                href={myProjectsLink.url}
                aria-label={myProjectsLink.text}
              >
                {myProjectsLink.image && (
                  <Image
                    className="site-header-link-icon site-header-icon-myprojects"
                    image={myProjectsLink.image}
                    ariaHidden={true}
                  />
                )}
                <Visible lg xl>
                  <span className="site-header-link-text swan-text-1">
                    <span className="site-header-link-primary">
                      {myProjectsLink.text}
                    </span>
                  </span>
                </Visible>
              </TrackableLink>
            </Visible>
          </Li>
        )}
        <Li padding={0} className="icon-alignment">
          <div className="site-header-link-menu site-header-link">
            <ErrorBoundary suppressError={suppressError} component="favorites">
              <FavoritesHeaderWithSSRFallback
                baseUrl={homeUrl}
                locale={locale}
                auth={auth}
                pageName={pageName}
                pageSection={pageSection}
                pageStage={pageStage}
              />
            </ErrorBoundary>
          </div>
        </Li>
        <Li padding={0} className="icon-alignment">
          <Visible sm md lg xl as="div">
            <Account accountLink={accountLink} auth={auth} />
          </Visible>
        </Li>
        <Li padding={0} className="icon-alignment">
          <div className="site-header-link-cart site-header-link-menu nohover">
            {isAuthInitialized && useMiniCartProp && getCartComponent() ? (
              getCartComponent()
            ) : (
              <div className="cart-item-count-container" data-testid="mini-cart-outer-container">
                <div className="cart-item-count" data-testid="mini-cart-container">
                  <TrackableLink
                    navigationDetailPath={[cartLink.analyticsId || 'cart']}
                    className="swan-link swan-link-skin-unstyled site-header-link"
                    href={cartLink.url}
                    aria-label={cartLink.text}
                  >
                    <div className="swan-display-flex swan-flex-box-align-items-center">
                      <div className="cart-icon site-header-link-icon site-header-icon-cart" data-testid="cart-icon">
                        <svg width="28" height="29" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0)">
                            <path d="M22 8.09863H6V24.7986H22V8.09863Z" stroke="black" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M11.5 10.6002V5.5002C11.5 4.8002 12.1 4.2002 12.8 4.2002H15.4C16.1 4.2002 16.7 4.8002 16.7 5.5002V10.7002" stroke="black" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                          </g>
                          <defs>
                            <clipPath id="clip0">
                              <rect width="24" height="24" fill="white" transform="translate(2 2.5)"/>
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                      <div>
                        <span className="swan-visually-hidden">{cartLink.text}</span>
                      </div>
                      <div className="cart-label" data-testid="cart-label" aria-hidden="true">
                        <Visible lg xl as="div">
                          <div className="swan-text-small">{cartLink.text}</div>
                        </Visible>
                      </div>
                    </div>
                  </TrackableLink>
                </div>
              </div>
            )}
          </div>
        </Li>
      </FlexBox>
    </nav>
  );
};

/**
 * Wrapper for FavoritesHeader that adds SSR fallback text
 * The FavoritesHeader component doesn't render text during SSR, only after hydration
 * This wrapper adds "My Favorites" text during SSR to match the aria-label
 */
const FavoritesHeaderWithSSRFallback = (props: any) => {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  // During SSR and initial render, show fallback with text
  if (!mounted) {
    return (
      <a
        href=""
        data-testid="favorites-header-link"
        aria-label="My Favorites"
        className="site-header-link-content site-header-link-menu-top-item"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="20" data-testid="favorites-header-heart-icon">
          <path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7z"></path>
        </svg>
        <Visible lg xl>
          <span className="site-header-link-text swan-text-1">
            <span className="site-header-link-primary">
              My Favorites
            </span>
          </span>
        </Visible>
      </a>
    );
  }

  // After mount, render the actual FavoritesHeader component
  return <FavoritesHeader {...props} />;
};
